import {PayloadAction} from '@reduxjs/toolkit'
import {IOrgAction, IUserLoggedAction, IUsersAction, UsersTypes} from './types'
import {UsersActions} from './index'
import {SnackbarActions, SnackBarMessageType} from '../snackbar'
import {all, apply, call, put, takeLatest} from 'redux-saga/effects'
import userService from '../../services/users'
import Messages from '../../assets/locales/pt/translation.json'
import {User} from '../../application/domain/models/entity/user'
import {Organization} from '../../application/domain/models/entity/organization'
import authService from '../../services/auth'
import {AccessToken} from '../../application/domain/models/auth/access.token'
import {UserType} from '../../application/domain/utils/user.type'
import {IActionSignup} from '../auth/types'

/**
 * <h5>Generator functions that make calls to the authentication service to obtain the data.</h5>
 * @see {@link https://developer.mozilla.org/pt-BR/docs/Web/JavaScript/Reference/Statements/function*}
 * @typedef UserSagas
 * @namespace UserSagas
 * @category React
 * @subcategory Redux / Sagas
 */

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.requestAdmin
 * @function
 * @category React
 * @subcategory Redux / Sagas
 */
export function* requestAdmin() {
    const { adminSuccess, adminFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const admins: Array<any> = yield apply(userService, userService.getAdmins, [])
        yield put(adminSuccess({
            data: admins
        }))
    } catch (err) {
        yield put(adminFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.getUserLogged
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IUserLoggedAction>} action User triggered action data.
 */
export function* getUserLogged(action: PayloadAction<IUserLoggedAction>) {
    const { userSuccess, userFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { userId, userType } = action.payload
        // @ts-ignore
        const user: User = yield apply(userService, userService.getUserById, [userType, userId])
        yield put(userSuccess({
            data: {
                name: user?.name || '',
                type: user?.type,
                email: user?.email || '',
                id: user?.id,
                cpf: user.cpf || '',
                organization_id: user.organization_id || '',
                organization_logo: user.organization_logo || '',
                institutional_position: user?.institutional_position || '',
                institutional_registration: user?.institutional_registration || '',
                phone: user.phone || '',
                uf_logo: user?.uf_logo || ''
            }
        }))
    } catch (err) {
        yield put(userFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.newAdmin
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IUsersAction>} action User triggered action data.
 */
export function* newAdmin(action: PayloadAction<IUsersAction>) {
    const { newAdminSuccess, newAdminFailure, adminRequest } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { name, email, password, organization_id, cpf, institutional_registration, institutional_position, phone } = action.payload
        const validFields = { name, email, password, organization_id, cpf }
        // @ts-ignore
        if (institutional_registration) validFields.institutional_registration = institutional_registration
        // @ts-ignore
        if (institutional_position) validFields.institutional_position = institutional_position
        // @ts-ignore
        if (phone) validFields.phone = phone

        // @ts-ignore
        yield apply(userService, userService.newAdmin, [new User().fromJSON(validFields)])
        yield put(newAdminSuccess())
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Usuário adicionado com sucesso.'
        }))
        yield put(adminRequest())
    } catch (err: any) {
        yield put(newAdminFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'Parameter {organization_id} is not in valid format!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.ORGANIZATION}`
                }))
            } if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'cpf must be a valid string of digits!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.CPF_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.editAdmin
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IUsersAction>} action User triggered action data.
 */
export function* editAdmin(action: PayloadAction<IUsersAction>) {
    const { editAdminSuccess, editAdminFailure, adminRequest } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { name, email, password, organization_id, cpf, institutional_registration, institutional_position, phone, id } = action.payload
        const validFields = { name, email, password, organization_id, cpf, id }
        // @ts-ignore
        if (institutional_registration) validFields.institutional_registration = institutional_registration
        // @ts-ignore
        if (institutional_position) validFields.institutional_position = institutional_position
        // @ts-ignore
        if (phone) validFields.phone = phone

        // @ts-ignore
        yield apply(userService, userService.editAdmin, [new User().fromJSON(validFields)])
        yield put(editAdminSuccess())
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Usuário editado com sucesso.'
        }))
        const token: AccessToken = authService.decodeToken()
        if (token.sub_type === UserType.ADMIN) yield put(adminRequest())
    } catch (err: any) {
        yield put(editAdminFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'cpf must be a valid string of digits!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.CPF_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.requestAnalyst
 * @function
 * @category React
 * @subcategory Redux / Sagas
 */
export function* requestAnalyst() {
    const { analystSuccess, analystFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const analysts: Array<any> = yield apply(userService, userService.getAnalysts, [])
        yield put(analystSuccess({
            data: analysts
        }))
    } catch (err) {
        yield put(analystFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.newAnalyst
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IUsersAction>} action User triggered action data.
 */
export function* newAnalyst(action: PayloadAction<IUsersAction>) {
    const { newAnalystSuccess, newAnalystFailure, analystRequest } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            name,
            email,
            password,
            organization_id,
            cpf,
            institutional_registration,
            institutional_position,
            phone,
            resources
        } = action.payload
        const validFields = { name, email, password, organization_id, cpf }
        // @ts-ignore
        if (institutional_registration) validFields.institutional_registration = institutional_registration
        // @ts-ignore
        if (institutional_position) validFields.institutional_position = institutional_position
        // @ts-ignore
        if (phone) validFields.phone = phone

        // @ts-ignore
        const user = yield apply(userService, userService.newAnalysts, [new User().fromJSON(validFields)])
        yield put(newAnalystSuccess())
        yield put(analystRequest())
        if (resources?.length !== 0) {
            // @ts-ignore
            yield call(changeResources, { payload: { id: user.id, resources } })
        }
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Usuário cadastrado com sucesso.'
        }))
    } catch (err: any) {
        yield put(newAnalystFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'Parameter {organization_id} is not in valid format!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.ORGANIZATION}`
                }))
            } if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'cpf must be a valid string of digits!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.CPF_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

/**
 * Generating function that calls the login service to authenticate the system.
 * @memberof UserSagas
 * @alias UserSagas.editAnalyst
 * @function
 * @category React
 * @subcategory Redux / Sagas
 * @param {PayloadAction<IUsersAction>} action User triggered action data.
 */
export function* editAnalyst(action: PayloadAction<IUsersAction>) {
    const { editAnalystSuccess, editAnalystFailure, analystRequest } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            name,
            email,
            password,
            organization_id,
            cpf,
            institutional_registration,
            institutional_position,
            phone,
            id,
            resources
        } = action.payload
        const validFields = { name, email, password, organization_id, cpf, id }
        // @ts-ignore
        if (institutional_registration) validFields.institutional_registration = institutional_registration
        // @ts-ignore
        if (institutional_position) validFields.institutional_position = institutional_position
        // @ts-ignore
        if (phone) validFields.phone = phone

        // @ts-ignore
        const user: User = yield apply(userService, userService.editAnalysts, [new User().fromJSON(validFields)])
        yield put(editAnalystSuccess({
            data: user
        }))
        if (resources && resources?.length !== 0) {
            // @ts-ignore
            yield call(changeResources, { payload: { id: user.id, resources } })
        }
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Usuário editado com sucesso.'
        }))
        const token: AccessToken = authService.decodeToken()
        if (token.sub_type === UserType.ADMIN) yield put(analystRequest())
    } catch (err: any) {
        yield put(editAnalystFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'cpf must be a valid string of digits!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.CPF_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* changeResources(action: PayloadAction<IUsersAction>) {
    const { changeResourcesFailure, changeResourcesSuccess } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            resources,
            id
        } = action.payload
        // @ts-ignore
        yield apply(userService, userService.changeResources, [id, resources])
        yield put(changeResourcesSuccess())
    } catch (err: any) {
        yield put(changeResourcesFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* newOrg(action: PayloadAction<IOrgAction>) {
    const { newOrgRequestSucess, newOrgRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            name,
            email,
            municipality,
            uf_id,
            responsible,
            responsible_sector,
            address,
            act_start_date,
            act_end_date,
            notes,
            phone
        } = action.payload

        const validFilters = { name, email, responsible, uf_id, municipality, act_start_date, act_end_date }

        // @ts-ignore
        if (responsible_sector) validFilters.responsible_sector = responsible_sector
        // @ts-ignore
        if (notes) validFilters.notes = notes
        // @ts-ignore
        if (phone) validFilters.phone = phone

        // @ts-ignore
        if (address) {
            // @ts-ignore
            validFilters.address = {}
            // @ts-ignore
            if (address.street) validFilters.address.street = address.street
            // @ts-ignore
            if (address.district) validFilters.address.district = address.district
            // @ts-ignore
            if (address.complement) validFilters.address.complement = address.complement
            // @ts-ignore
            if (address.zip_code) validFilters.address.zip_code = address.zip_code
        }

        // @ts-ignore
        const org = yield apply(userService, userService.newOrg, [new Organization().fromJSON(validFilters)])
        yield put(newOrgRequestSucess({
            data: {
                idOrg: org.id
            }
        }))
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Organização cadastrada com sucesso.'
        }))
        yield call(getAllOrgs)
    } catch (err: any) {
        yield put(newOrgRequestFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'Invalid email address!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* updateOrg(action: PayloadAction<IOrgAction>) {
    const { orgsUpdateRequestSucess, orgsUpdateRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const {
            name,
            email,
            municipality,
            uf_id,
            responsible,
            responsible_sector,
            address,
            act_start_date,
            act_end_date,
            notes,
            phone,
            id
        } = action.payload

        const validFilters = { id }

        // @ts-ignore
        if (name) validFilters.name = name
        // @ts-ignore
        if (email) validFilters.email = email
        // @ts-ignore
        if (municipality) validFilters.municipality = municipality
        // @ts-ignore
        if (uf_id) validFilters.uf_id = uf_id
        // @ts-ignore
        if (responsible) validFilters.responsible = responsible
        // @ts-ignore
        if (responsible_sector) validFilters.responsible_sector = responsible_sector
        // @ts-ignore
        if (notes) validFilters.notes = notes
        // @ts-ignore
        if (phone) validFilters.phone = phone
        // @ts-ignore
        if (act_start_date) validFilters.act_start_date = act_start_date
        // @ts-ignore
        if (act_end_date) validFilters.act_end_date = act_end_date

        // @ts-ignore
        if (address) {
            // @ts-ignore
            validFilters.address = {}
            // @ts-ignore
            if (address.street) validFilters.address.street = address.street
            // @ts-ignore
            if (address.district) validFilters.address.district = address.district
            // @ts-ignore
            if (address.complement) validFilters.address.complement = address.complement
            // @ts-ignore
            if (address.zip_code) validFilters.address.zip_code = address.zip_code
        }

        // @ts-ignore
        yield apply(userService, userService.updateOrg, [new Organization().fromJSON(validFilters)])
        yield put(orgsUpdateRequestSucess())
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Organização editada com sucesso.'
        }))
    } catch (err: any) {
        yield put(orgsUpdateRequestFailure())
        if (err.response.status >= 400 && err.response.status <= 499) {
            if (err.response.data.message === 'A user with this email is already registered!') {
                yield put(openSnackBar({
                    title: 'Email ja registrado.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_REGISTERD}`
                }))
            } if (err.response.data.description === 'Invalid email address!') {
                yield put(openSnackBar({
                    title: 'Campo nulo.',
                    type: SnackBarMessageType.WARNING,
                    message: `${Messages.REQUEST.USERS.REQUEST.EMAIL_NULL}`
                }))
            }
        } if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* getAllOrgs() {
    const { orgsRequestSucess, orgsRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const result = yield apply(userService, userService.getAll, [])
        yield put(orgsRequestSucess({
            data: result
        }))
    } catch (err) {
        yield put(orgsRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* getUsersByOrg(action: PayloadAction<IOrgAction>) {
    const { getUersByOrgSucess, getUersByOrgFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id } = action.payload
        // @ts-ignore
        const result = yield apply(userService, userService.getUsersByOrg, [id])
        yield put(getUersByOrgSucess({
            data: result
        }))
    } catch (err) {
        yield put(getUersByOrgFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* orgResponsibles(action: PayloadAction<IOrgAction>) {
    const { orgResponsiblesSucess, orgResponsiblesFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id, userId } = action.payload
        // @ts-ignore
        yield apply(userService, userService.orgResponsibles, [id, userId])
        yield put(orgResponsiblesSucess())
    } catch (err) {
        yield put(orgResponsiblesFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* userActive(action: PayloadAction<IUsersAction>) {
    const {
        userActiveFailure,
        userActiveSucess,
        adminRequest,
        analystRequest
    } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id, active } = action.payload
        // @ts-ignore
        yield apply(userService, userService.userActive, [id, active])
        yield put(userActiveSucess())
        yield put(openSnackBar({
            title: 'Sucesso',
            type: SnackBarMessageType.SUCCESS,
            message: 'Usuário Desativado com Sucesso.'
        }))
        yield put(adminRequest())
        yield put(analystRequest())
    } catch (err: any) {
        yield put(userActiveFailure())
        yield put(openSnackBar({
            title: 'Erro Interno',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* uploadAct(action: PayloadAction<IOrgAction>) {
    const { orgsActUploadSuccess, orgsActUploadFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { act, id } = action.payload
        // @ts-ignore
        yield apply(userService, userService.actUpload, [act, id])
        yield put(orgsActUploadSuccess())
        yield put(openSnackBar({
            title: 'Sucesso',
            type: SnackBarMessageType.SUCCESS,
            message: 'Arquivo salvo com Sucesso.'
        }))
    } catch (err: any) {
        yield put(orgsActUploadFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* download(action: PayloadAction<IOrgAction>) {
    const { orgsActDownloadSuccess, orgsActDownloadFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id } = action.payload
        // @ts-ignore
        const file = yield apply(userService, userService.actDownload, [id])
        yield put(orgsActDownloadSuccess())
        const blob = new Blob([file], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'act.pdf'
        a.click()
        window.URL.revokeObjectURL(url)
    } catch (err: any) {
        yield put(orgsActDownloadFailure())
        if (err.response.status >= 500 && err.response.status <= 599) {
            yield put(openSnackBar({
                title: 'Erro Interno.',
                type: SnackBarMessageType.ERROR,
                message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
            }))
        }
    }
}

export function* analystRequest(action: PayloadAction<IActionSignup>) {
    const { signupRequestSuccess, signupRequestFailure } = UsersActions
    const { openDialog, openSnackBar } = SnackbarActions
    try {
        yield apply(userService, userService.signup, [action.payload])
        yield put(signupRequestSuccess())
        yield put(openDialog({
            title: "Sua solicitação foi recebida e será analisada pela administração da plataforma.",
            message: "Cheque periodicamente a caixa de entrada e o SPAM do email institucional informado para saber se seu acesso foi admitido.",
            to: "/login"
        }))
    } catch (err: any) {
        yield put(signupRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.PERSON.REQUEST.INTERNAL}`
        }))
    }
}

export function* usersRequest() {
    const { usersRequestSuccess, usersRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const result = yield apply(userService, userService.usersRequest, [])
        yield put(usersRequestSuccess({
            data: result
        }))
    } catch (err) {
        yield put(usersRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* deleteUserRequest(action: PayloadAction<IUsersAction>) {
    const { deleteUsersRequestSuccess, deleteUsersRequestFailure, usersRequest } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id } = action.payload
        yield apply(userService, userService.deleteUserRequest, [id])
        yield put(deleteUsersRequestSuccess())
        yield put(openSnackBar({
            type: SnackBarMessageType.SUCCESS,
            title: "Sucesso na Operação",
            message: "Operação realizada com sucesso."
        }))
        yield put(usersRequest())
    } catch (err: any) {
        yield put(deleteUsersRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* findUserRequest(action: PayloadAction<{ id: string }>) {
    const { findOneUserRequestSuccess, findOneUserRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const data = yield apply(userService, userService.findOneUserRequestr, [action.payload.id])
        yield put(findOneUserRequestSuccess({ data }))
    } catch (err: any) {
        yield put(findOneUserRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* orgName() {
    const { orgNameRequestFailure, orgNameRequestSuccess } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const data = yield apply(userService, userService.findOrgNameRequest, [])
        yield put(orgNameRequestSuccess({ data }))
    } catch (err: any) {
        yield put(orgNameRequestFailure())
        yield put(openSnackBar({
            title: 'Erro Interno.',
            type: SnackBarMessageType.ERROR,
            message: `${Messages.REQUEST.USERS.REQUEST.INTERNAL}`
        }))
    }
}

export function* ufs() {
    const { ufRequestFailure, ufRequestSuccess } = UsersActions
    try {
        const data = yield apply(userService, userService.ufRequest, [])
        yield put(ufRequestSuccess({ data }))
    } catch (err: any) {
        yield put(ufRequestFailure())
    }
}

export function* uploadLogo(action: PayloadAction<IOrgAction>) {
    const { orgLogoRequestSuccess, orgLogoRequestFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { logo, id } = action.payload// @ts-ignore
        yield apply(userService, userService.logoUpload, [logo, id])
        yield put(orgLogoRequestSuccess())
        yield put(openSnackBar({
            title: 'Sucesso.',
            type: SnackBarMessageType.SUCCESS,
            message: 'Imagem enviada com sucesso.'
        }))
    } catch (err: any) {
        yield put(orgLogoRequestFailure())
        yield put(openSnackBar({ type: SnackBarMessageType.ERROR, title: 'Erro.', message: 'Ocorreu um erro ao enviar a imagem.' }))
    }
}

export function* logsUser(action: PayloadAction<IUsersAction>) {
    const { logsUserSuccess, logsUserFailure } = UsersActions
    const { openSnackBar } = SnackbarActions
    try {
        const { id } = action.payload
        const data = yield apply(userService, userService.logsRequest, [`${id}`])
        yield put(logsUserSuccess({ data }))
    } catch (err: any) {
        yield put(logsUserFailure())
        yield put(openSnackBar({ type: SnackBarMessageType.ERROR, title: 'Erro.', message: 'Ocorreu um erro ao carregar os logs.' }))
    }
}

const UserSagas = function* () {
    yield all([
        takeLatest(UsersTypes.USER_REQUEST, getUserLogged),
        takeLatest(UsersTypes.ADMIN_REQUEST, requestAdmin),
        takeLatest(UsersTypes.NEW_ADMIN, newAdmin),
        takeLatest(UsersTypes.EDIT_ADMIN, editAdmin),
        takeLatest(UsersTypes.ANALYST_REQUEST, requestAnalyst),
        takeLatest(UsersTypes.NEW_ANALYST, newAnalyst),
        takeLatest(UsersTypes.EDIT_ANALYST, editAnalyst),
        takeLatest(UsersTypes.NEW_ORG, newOrg),
        takeLatest(UsersTypes.ORGS_UPDATE_REQUEST, updateOrg),
        takeLatest(UsersTypes.ORGS_REQUEST, getAllOrgs),
        takeLatest(UsersTypes.ORGS_USERS_REQUEST, getUsersByOrg),
        takeLatest(UsersTypes.ORGS_RESPONSIBLES_REQUEST, orgResponsibles),
        takeLatest(UsersTypes.USER_ACTIVE_REQUEST, userActive),
        takeLatest(UsersTypes.ORGS_ACT_UPLOAD, uploadAct),
        takeLatest(UsersTypes.ORGS_ACT_DOWNLOAD, download),
        takeLatest(UsersTypes.SIGNUP_REQUEST, analystRequest),
        takeLatest(UsersTypes.USERS_REQUEST, usersRequest),
        takeLatest(UsersTypes.FIND_USER_REQUEST, findUserRequest),
        takeLatest(UsersTypes.DELETE_REQUEST, deleteUserRequest),
        takeLatest(UsersTypes.ORG_NAME_REQUEST, orgName),
        takeLatest(UsersTypes.UF_REQUEST, ufs),
        takeLatest(UsersTypes.ORG_LOGO_REQUEST, uploadLogo),
        takeLatest(UsersTypes.LOGOS_USER_REQUEST, logsUser),
    ])
}

export default UserSagas
